import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ShieldCheck, Loader2, Mail, Lock } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * /family/accept?token=X
 * Parent lands here from the magic-link email. Two states:
 *   1. Account doesn't exist → set name + password, auto-login on accept.
 *   2. Account already exists → one-click accept (auto-login via magic token).
 */
export default function FamilyAccept() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const { setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) { setError("This link is missing its token. Ask your student to resend the invite."); return; }
    api.familyInvitePreview(token)
      .then(({ data }) => { setPreview(data); if (data.parent_name_hint) setName(data.parent_name_hint); })
      .catch((e) => setError(errMsg(e)));
  }, [token]);

  const accept = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const body = { token };
      if (preview && !preview.parent_exists) {
        if (!name.trim() || !password.trim() || password.length < 6) {
          toast.error("Add your name and set a password (6+ characters).");
          setBusy(false); return;
        }
        body.name = name.trim();
        body.password = password;
      }
      const { data } = await api.familyAccept(body);
      localStorage.setItem("hb_token", data.token);
      setUser(data.user);
      toast.success(`Welcome — you're linked to ${data.linked_student_name}. ✨`);
      navigate("/account");
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setBusy(false); }
  };

  if (error) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="family-accept-error">
        <div className="max-w-md text-center">
          <p className="font-label text-[10px] tracking-widest text-rosemetal">FAMILY INVITE</p>
          <h1 className="font-display text-4xl mt-3 leading-tight">This link isn't working.</h1>
          <p className="font-body text-sm text-ink/60 mt-4">{error}</p>
          <Link to="/" className="mt-6 inline-block bg-ink text-ivory font-label text-[10px] tracking-[0.25em] px-6 py-3 rounded-sm hover:bg-rosemetal transition-colors">GO HOME</Link>
        </div>
      </div>
    );
  }

  if (!preview) {
    return <div className="min-h-[70vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-rosemetal" /></div>;
  }

  const relPretty = { mom: "Mom", dad: "Dad", guardian: "Guardian", grandparent: "Grandparent" }[preview.relationship] || "Parent";

  return (
    <div data-testid="family-accept-page" className="min-h-[80vh] flex items-center justify-center py-16 px-6 bg-gradient-to-b from-blush/40 to-ivory">
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full bg-ivory rounded-sm border border-border p-8 sm:p-10 shadow-[0_25px_60px_-20px_rgba(10,10,10,0.15)]"
      >
        <div className="text-center mb-6">
          <div className="mx-auto w-14 h-14 rounded-full bg-cream border border-rosemetal/30 flex items-center justify-center mb-4">
            <img src="/heathers-h.png" alt="" className="w-8 h-8 object-contain" />
          </div>
          <p className="font-label text-[10px] tracking-widest text-rosemetal">A FAMILY INVITE</p>
          <h1 className="font-display text-4xl leading-tight mt-2">
            {preview.student_name} added you<br />as their <span className="italic text-rosemetal">{relPretty}</span>.
          </h1>
          {preview.student_school_name && (
            <p className="font-body text-sm text-ink/60 mt-3">from <b className="text-ink">{preview.student_school_name}</b></p>
          )}
        </div>

        <div className="bg-blush/30 border border-rosemetal/25 rounded-sm p-4 mb-6 text-left">
          <p className="font-label text-[9px] tracking-widest text-rosemetal mb-2">ONCE YOU'RE IN</p>
          <ul className="space-y-1.5 font-body text-[13px] text-ink/80">
            <li className="flex gap-2"><span className="text-rosemetal">◦</span> Shop on behalf of {preview.student_name} — locks credit to them</li>
            <li className="flex gap-2"><span className="text-rosemetal">◦</span> View their AI try-on lookbook</li>
            <li className="flex gap-2"><span className="text-rosemetal">◦</span> Book a private fitting at the boutique</li>
          </ul>
        </div>

        <form onSubmit={accept} className="space-y-3">
          <div>
            <label className="font-label text-[9px] tracking-widest text-ink/60">YOUR EMAIL</label>
            <div className="mt-1 flex items-center gap-2 border border-border rounded-sm px-4 py-3 bg-cream/60">
              <Mail className="w-3.5 h-3.5 text-ink/50" />
              <span className="font-body text-sm text-ink">{preview.parent_email}</span>
            </div>
          </div>

          {!preview.parent_exists && (
            <>
              <div>
                <label className="font-label text-[9px] tracking-widest text-ink/60">YOUR NAME</label>
                <input
                  type="text" required
                  value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Mom · Dad · Guardian…"
                  data-testid="accept-name"
                  className="mt-1 w-full border border-border rounded-sm px-4 py-3 font-body text-sm outline-none focus:border-rosemetal transition-colors"
                />
              </div>
              <div>
                <label className="font-label text-[9px] tracking-widest text-ink/60">SET A PASSWORD</label>
                <input
                  type="password" required minLength={6}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="6+ characters"
                  data-testid="accept-password"
                  className="mt-1 w-full border border-border rounded-sm px-4 py-3 font-body text-sm outline-none focus:border-rosemetal transition-colors"
                />
              </div>
            </>
          )}

          <button
            type="submit" disabled={busy}
            data-testid="accept-submit"
            className="w-full bg-ink text-ivory font-label text-[11px] tracking-[0.28em] py-4 rounded-sm hover:bg-rosemetal transition-colors flex items-center justify-center gap-2 disabled:opacity-60 mt-2"
          >
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> LINKING…</> : (
              <><ShieldCheck className="w-4 h-4" /> ACCEPT INVITE {preview.parent_exists && <Lock className="w-3 h-3 opacity-60" />}</>
            )}
          </button>
          {preview.parent_exists && (
            <p className="text-center font-label text-[9px] tracking-widest text-ink/50">SIGN IN AUTOMATICALLY · NO PASSWORD PROMPT</p>
          )}
        </form>
      </motion.div>
    </div>
  );
}
